import type { CSSProperties, ReactNode } from 'react';
import clsx from 'clsx';
import { resolveResponsiveValue, type ResponsiveValue } from '@publishing/helpers/responsive';
import s from './Title.module.scss';

/**
 * Title — 시맨틱 제목(h1~h6)에 name·size 조합으로 타이포 클래스를 붙인다.
 *
 * [디폴트] — 해당 prop을 넘기지 않을 때 적용되는 값
 * - `level`: 2 → 렌더 태그는 `<h2>`
 * - `name`: `"headline"`
 * - `size`: `"100"` (100이 가장 크고, 300이 가장 작음)
 * - `weight`: 700 (400·500·600·700 중 선택, `_variables.scss`의 `.weight_*`와 동일)
 * - `align`: 단독 사용 시 `"center"` (left · center · right)
 *
 * [사용 예]
 * ```tsx
 * <Title>기본 제목</Title>
 * <Title level={1} name="display" size="100">메인 타이틀</Title>
 * <Title name="headline" size="300">작은 제목</Title>
 * <Title weight={500}>중간 굵기</Title>
 * <Title align="left">왼쪽 정렬</Title>
 * <Title align={['left', 'center']}>PC 왼쪽 / MO 가운데 정렬</Title>
 * <TitleGroup align="left">
 *   <Title level={1}>TitleGroup 정렬을 따르는 제목</Title>
 *   <p>부가 설명</p>
 * </TitleGroup>
 * ```
 *
 * 타이포 클래스는 `` `font_${name}_${size}` `` 로 붙는다. (예: 생략 시 `font_headline_100`, `.ff-mkt` 필요)
 */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export type TitleName = 'display' | 'headline';

/** 100(가장 큼) · 200 · 300(가장 작음) */
export type TitleSize = '100' | '200' | '300';

export type TitleWeight = 400 | 500 | 600 | 700;

/** `` `font_${name}_${size}` `` 로 생성되는 타이포 클래스명 */
export type TitleTypographyClass = `font_${TitleName}_${TitleSize}`;

export type TitleAlign = 'left' | 'center' | 'right';
export type TitleResponsiveAlign = ResponsiveValue<TitleAlign>;

interface TitleProps {
  children: ReactNode;
  /** 시맨틱 제목 단계 (기본: 2 → h2) */
  level?: HeadingLevel;
  className?: string;
  /** 기본 headline. display 시 디스플레이 스케일 */
  name?: TitleName;
  /** 크기 100·200·300 (기본 100) */
  size?: TitleSize;
  /** 폰트 굵기 (기본 700) */
  weight?: TitleWeight;
  /** 텍스트 정렬 */
  align?: TitleResponsiveAlign;
}

/**
 * 제목 컴포넌트. `level`로 태그만 선택 (기본 h2).
 * name·size·weight는 data 속성으로, align은 클래스명으로 전달해 SCSS에서 분기 처리.
 */
export function Title({
  children,
  level = 2,
  className,
  name = 'headline',
  size = '100',
  weight = 700,
  align,
  ...rest
}: TitleProps & Omit<React.HTMLAttributes<HTMLHeadingElement>, 'title'>) {
  const Tag = `h${level}` as const;
  const [pcAlign, moAlign] = align ? resolveResponsiveValue(align) : [];
  const alignClassName = pcAlign ? s[`title_${pcAlign}`] : '';
  const mobileAlignClassName = moAlign ? s[`title_mo_${moAlign}`] : '';

  return (
    <Tag
      className={clsx(s.title, alignClassName, mobileAlignClassName, className)}
      data-name={name}
      data-size={size}
      data-weight={weight}
      {...rest}
    >
      {children}
    </Tag>
  );
}

interface TitleGroupProps {
  children: ReactNode;
  className?: string;
  /** 자식 간격(px). 단일값은 PC/MO 공통, 배열은 [PC, MO]. 생략 시 기존 배치 유지 */
  gap?: number | readonly [pc: number, mo: number];
  /** 블록 정렬 (기본 center), 배열 입력 시 [PC, MO] 순서 */
  align?: TitleResponsiveAlign;
}

/**
 * 제목 블록 래퍼. 시맨틱 `<header>`로 `Title`과 부가 문구(`p` 등)를 묶는다.
 * 정렬 기본값은 center.
 */
export function TitleGroup({
  children,
  className,
  align,
  gap,
  style,
  ...rest
}: TitleGroupProps & Omit<React.HTMLAttributes<HTMLElement>, 'children'>) {
  const [pcAlign, moAlign] = resolveResponsiveValue(align);
  const alignClassName = pcAlign ? s[`title_group_${pcAlign}`] : '';
  const mobileAlignClassName = moAlign ? s[`title_group_mo_${moAlign}`] : '';
  const [pcGap, moGap = pcGap] = resolveResponsiveValue(gap);
  const groupStyle = {
    '--title-group-gap-pc': pcGap === undefined ? undefined : `${pcGap / 16}rem`,
    '--title-group-gap-mo': moGap === undefined ? undefined : `${moGap / 16}rem`,
    ...style,
  } as CSSProperties;

  return (
    <header
      className={clsx(s.title_group, alignClassName, mobileAlignClassName, gap !== undefined && s.is_gap, className)}
      style={groupStyle}
      {...rest}
    >
      {children}
    </header>
  );
}
